// Paleta de colores para roles de usuarios
// Basada en los colores ya utilizados en el diseño de la aplicación

export interface RoleColorConfig {
	background: string
	text: string
	label?: string
	/**
	 * Qué puede hacer el rol, en una línea, para el selector de roles.
	 * Sólo están las tres que redactó el cliente; un rol sin `description`
	 * (supervisor y los personalizados de cada empresa) no pinta nada.
	 */
	description?: string
}

// Paleta de colores específica para roles con jerarquía
const ROLE_HIERARCHY_COLORS: Record<string, RoleColorConfig> = {
	// MASTER - Rojo (máxima autoridad, color fuerte y distintivo)
	master: {
		background: 'bg-red-100',
		text: 'text-red-800',
		label: 'Administrador',
		description:
			'Puede administrar usuarios, políticas y reportes de la empresa.',
	},

	// TRAVEL MANAGER - Azul (gestión y coordinación)
	'travel manager': {
		background: 'bg-blue-100',
		text: 'text-blue-800',
		label: 'Gestor de Viajes',
		description:
			'Puede administrar usuarios, reservaciones, políticas y reportes de la empresa.',
	},

	// SUPERVISOR - Verde (supervisión y control)
	supervisor: {
		background: 'bg-green-100',
		text: 'text-green-800',
		label: 'Supervisor',
		// Redactada a partir de sus permisos reales: aprobaciones (viewAny/update
		// Approval), crear solicitudes y administrar datos de usuarios — todo
		// acotado a su línea jerárquica. NO tiene reportes, a diferencia de Travel
		// Manager y Administrador.
		description:
			'Puede aprobar las solicitudes y administrar los datos de los usuarios a su cargo.',
	},

	// TRAVELER - Cian (usuario final, color suave y claramente diferenciado)
	traveler: {
		background: 'bg-cyan-100',
		text: 'text-cyan-800',
		label: 'Viajero',
		description:
			'Cualquier colaborador de la empresa que realice viajes. Puede ver todos sus viajes.',
	},
}

// Paleta de colores de respaldo para roles no definidos
const FALLBACK_COLORS: RoleColorConfig[] = [
	{
		background: 'bg-purple-100',
		text: 'text-purple-800',
		label: 'Rol personalizado',
	},
	{
		background: 'bg-indigo-100',
		text: 'text-indigo-800',
		label: 'Rol personalizado',
	},
	{
		background: 'bg-pink-100',
		text: 'text-pink-800',
		label: 'Rol personalizado',
	},
	{
		background: 'bg-red-100',
		text: 'text-red-800',
		label: 'Rol personalizado',
	},
	{
		background: 'bg-orange-100',
		text: 'text-orange-800',
		label: 'Rol personalizado',
	},
	{
		background: 'bg-teal-100',
		text: 'text-teal-800',
		label: 'Rol personalizado',
	},
]

// Cache para mantener consistencia en roles no definidos
const roleColorCache = new Map<string, RoleColorConfig>()

/**
 * Obtiene la configuración de colores para un rol específico
 * @param roleName - Nombre del rol
 * @returns Configuración de colores (background y text)
 */
export function getRoleColor(roleName: string): RoleColorConfig {
	if (!roleName) {
		return { background: 'bg-gray-100', text: 'text-gray-600' }
	}

	const normalizedRole = roleName.toLowerCase().trim()

	// Verificar si es uno de los roles de jerarquía definidos
	if (ROLE_HIERARCHY_COLORS[normalizedRole]) {
		return ROLE_HIERARCHY_COLORS[normalizedRole]
	}

	// Para roles no definidos, usar cache o asignar nuevo color
	if (roleColorCache.has(normalizedRole)) {
		return roleColorCache.get(normalizedRole)!
	}

	// Asignar color de respaldo basado en hash del nombre
	const hash = normalizedRole.split('').reduce((acc, char) => {
		return char.charCodeAt(0) + ((acc << 5) - acc)
	}, 0)

	const colorIndex = Math.abs(hash) % FALLBACK_COLORS.length
	const color = FALLBACK_COLORS[colorIndex]

	roleColorCache.set(normalizedRole, color)
	return color
}

/**
 * Obtiene las clases CSS completas para un rol
 * @param roleName - Nombre del rol
 * @returns String con las clases CSS de background y text
 */
export function getRoleClasses(roleName: string): {
	class: string
	label?: string
	description?: string
} {
	const colors = getRoleColor(roleName)

	return {
		class: `${colors.background} ${colors.text}`,
		label: colors.label,
		description: colors.description,
	}
}

/**
 * Limpia el cache de colores de roles
 */
export function clearRoleColorCache(): void {
	roleColorCache.clear()
}
